// stores/domains/additionalInfo.ts

import { clearSidebarConfigCache } from "../../../routers/utils";
import type { AsyncStoreActions, BaseStore } from "../../core";
import { createAsyncActions, createStore, withLogging } from "../../core";
import { USER_ROLES } from "./constants";

export type { UserRole } from "./constants";
// Re-export constants for convenience
export { USER_ROLES } from "./constants";

export type LabeledValue = { label: string; value: string };

type AdditionalValues = {
  organization: LabeledValue | null;
  location: LabeledValue | null;
  room: LabeledValue | null;
  role: LabeledValue | null;
  terminal: LabeledValue | null;
  // SHA-256 fingerprint of the user this additional info belongs to (never the plaintext id)
  userId?: string | null;
};

interface IAdditionalInformationState extends BaseStore, AsyncStoreActions, AdditionalValues {
  setAdditionalInformationAction: (value: AdditionalValues) => void;
  setUserId: (userId: string) => void;
  reset: () => void;
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

const initialState: AdditionalValues = {
  organization: null,
  location: null,
  room: null,
  role: null,
  terminal: null,
  userId: null,
};

export const useAdditionalInfo = createStore<IAdditionalInformationState>(
  withLogging("UseAdditionalInfo", (set) => ({
    ...initialState,
    ...createAsyncActions<IAdditionalInformationState>(),
    _initialized: false,
    _lastUpdated: Date.now(),
    _hasHydrated: false,
    setAdditionalInformationAction: (value) => {
      set({
        ...value,
        _initialized: true,
        _lastUpdated: Date.now(),
        _hasHydrated: true,
      });
      clearSidebarConfigCache();
    },
    setUserId: (userId: string) => {
      set({ userId });
    },
    setHasHydrated: (state: boolean) => {
      set({ _hasHydrated: state });
    },
    reset: () => {
      set(
        () => ({
          ...initialState,
          _initialized: false,
          _lastUpdated: Date.now(),
          _hasHydrated: true,
        }),
        true,
      );
      clearSidebarConfigCache();
    },
  })),
  {
    name: "user-additional-store",
    persist: true,
    version: 2,
    partialize: (state) => ({
      organization: state.organization,
      location: state.location,
      room: state.room,
      role: state.role,
      terminal: state.terminal,
      userId: state.userId,
      _hasHydrated: state._hasHydrated,
    }),
    onRehydrateStorage: () => {
      return (state: IAdditionalInformationState | undefined, error: unknown) => {
        if (error) {
          state?.setHasHydrated(true);
        } else {
          state?.setHasHydrated(true);
          clearSidebarConfigCache();
        }
      };
    },
  },
);

export const useAdditionalInfoActions = () =>
  useAdditionalInfo((state) => ({
    clearError: state.clearError,
    setAdditionalInformationAction: state.setAdditionalInformationAction,
    reset: state.reset,
  }));

export const getHospitalClinic = () => useAdditionalInfo.getState().location;
export const getOrganization = () => useAdditionalInfo.getState().organization;
export const getUserId = () => useAdditionalInfo.getState().userId;
export const getTerminal = () => useAdditionalInfo.getState().terminal;
export const isDoctor = () => {
  const label = useAdditionalInfo.getState().role?.label;
  return typeof label === "string" && label.toLowerCase() === USER_ROLES.DOCTOR;
};
export const isDoctorOrDoctorAdmin = () => {
  const label = useAdditionalInfo.getState().role?.label;
  if (typeof label !== "string") return false;
  const lowerLabel = label.toLowerCase();
  return lowerLabel === "doctor" || lowerLabel === "doctor admin";
};
export const getRoom = () => useAdditionalInfo.getState().room;
